// Recovery when a lazy chunk fails to load. With an installed app that is
// almost always a file an older build named and a later deploy removed: the
// page still runs the build the service worker cached, the server only has
// the new build's files, and the screen that needed the chunk stays broken
// (2026-09-29: an iPhone asked for react-flow-CnUS1RER.js twice after the
// day's eighth deploy; the live build's is react-flow-BMked20e.js). So: fetch
// and switch to the build the server has, then reload once. A second failure
// within RETRY_GAP_MS is left to surface (a genuinely missing file or no
// network must not reload in a loop).
//
// Never while a meeting is recorded or its parts wait to go in this page
// (recordingHold.ts): a reload stops the microphone, and in the Mac app
// (no service worker, no beforeunload panel) nothing would ask first. The
// failure is then left to the screen's own error ("Reload page"), and the
// recovery reload happens once the recording lets go, unless a reload would
// then lose something else (afterRecording).

import { recordingActive, subscribeRecordingActive } from "@/features/meetings/recorder/recordingHold";
import { heldHere } from "./updateHold";

const KEY = "lattice.pwa.chunkReloadAt";
export const RETRY_GAP_MS = 60_000;

/** Reload for a failed chunk only when the last such reload is at least RETRY_GAP_MS old. */
export function shouldRecover(lastReloadAt: number, now: number): boolean {
  return !(lastReloadAt > 0 && now - lastReloadAt < RETRY_GAP_MS);
}

/** What a failed chunk does now: reload, wait for the recording to let go, or leave it to surface. */
export function chunkFailure(lastReloadAt: number, now: number, recording: boolean): "reload" | "wait" | "leave" {
  if (!shouldRecover(lastReloadAt, now)) return "leave";
  return recording ? "wait" : "reload";
}

/**
 * The recording has let go of a reload that waited for it: reload now only
 * when nothing else on the page would be lost (a message being typed, an
 * unsent file: updateHold.heldHere); otherwise leave it, and the next
 * failed chunk recovers in the ordinary way.
 */
export function afterRecording(lastReloadAt: number, now: number, held: boolean): "reload" | "leave" {
  return !held && shouldRecover(lastReloadAt, now) ? "reload" : "leave";
}

function lastReload(): number {
  try {
    return Number(sessionStorage.getItem(KEY) || 0) || 0;
  } catch {
    return 0;
  }
}

async function switchToLatestBuild(): Promise<void> {
  const reg = await navigator.serviceWorker?.getRegistration?.().catch(() => undefined);
  if (!reg) return;
  await reg.update().catch(() => undefined);
  const next = reg.waiting ?? reg.installing;
  if (!next) return;
  // The same message PwaUpdatePrompt sends to apply a waiting build; wait
  // (bounded) for it to take over so the reload loads the new files.
  await new Promise<void>((resolve) => {
    const done = () => resolve();
    navigator.serviceWorker.addEventListener("controllerchange", done, { once: true });
    setTimeout(done, 4_000);
    next.postMessage({ type: "SKIP_WAITING" });
  });
}

function recover(): void {
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    /* no storage: still recover once per page */
  }
  void switchToLatestBuild().finally(() => window.location.reload());
}

let waiting: (() => void) | null = null;

export function installChunkRecovery(): void {
  window.addEventListener("vite:preloadError", (event) => {
    const what = chunkFailure(lastReload(), Date.now(), recordingActive());
    if (what === "leave") return;
    if (what === "wait") {
      // Not prevented: the screen shows its error now; the reload waits for the recording.
      waiting ??= subscribeRecordingActive(() => {
        if (recordingActive() || !waiting) return;
        waiting();
        waiting = null;
        if (afterRecording(lastReload(), Date.now(), heldHere()) === "reload") recover();
      });
      return;
    }
    event.preventDefault();
    recover();
  });
}
